import { SERVICE_LABELS } from "../lib/format";
import type { SearchFilters, ServiceKey } from "../lib/types";

interface Props {
  filters: SearchFilters;
  setFilters: (
    updater: SearchFilters | ((f: SearchFilters) => SearchFilters)
  ) => void;
}

function Pill({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 inline-flex items-center gap-1 px-4 py-1.5 text-sm rounded-full border transition ${
        active
          ? "bg-black text-white border-black"
          : "bg-white text-slate-800 border-slate-200 hover:border-slate-400"
      }`}
    >
      {children}
    </button>
  );
}

export function ChipStrip({ filters, setFilters }: Props) {
  const setF = <K extends keyof SearchFilters>(
    key: K,
    value: SearchFilters[K]
  ) => setFilters((f) => ({ ...f, [key]: value }));
  const isServiceKey = (value: string): value is ServiceKey =>
    Object.prototype.hasOwnProperty.call(SERVICE_LABELS, value);

  return (
    <div className="bg-white border-b border-slate-100">
      <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-8 py-3 overflow-x-auto">
        <div className="flex gap-2 items-center min-w-max">
          <Pill
            active={!!filters.open_now}
            onClick={() => setF("open_now", !filters.open_now)}
          >
            Open Now <span className="text-[10px]">▾</span>
          </Pill>
          <select
            value={(filters.services && filters.services[0]) || ""}
            onChange={(e) => {
              const value = e.target.value;
              setF("services", value && isServiceKey(value) ? [value] : []);
            }}
            className="shrink-0 text-sm border border-slate-200 rounded-full px-3 py-1.5 bg-white"
          >
            <option value="">All services</option>
            {Object.entries(SERVICE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <select
            value={filters.country ?? ""}
            onChange={(e) => setF("country", e.target.value || undefined)}
            className="shrink-0 text-sm border border-slate-200 rounded-full px-3 py-1.5 bg-white"
          >
            <option value="">All countries</option>
            <option value="US">United States</option>
            <option value="MX">Mexico</option>
            <option value="GB">United Kingdom</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="ES">Spain</option>
            <option value="CA">Canada</option>
          </select>
          <Pill
            active={!!filters.money_order_only}
            onClick={() => setF("money_order_only", !filters.money_order_only)}
          >
            Money Order Only
          </Pill>
          <Pill
            active={!!filters.crypto_eligible}
            onClick={() => setF("crypto_eligible", !filters.crypto_eligible)}
          >
            Crypto-Eligible
          </Pill>
          <Pill
            active={!!filters.topRated}
            onClick={() => setF("topRated", !filters.topRated)}
          >
            Top Rated
          </Pill>
          <button
            onClick={() => setFilters({})}
            className="shrink-0 text-xs text-slate-500 hover:text-slate-800 underline ml-2"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
